import React from "react";
import ReactDOM from "react-dom";

import { Node } from "prosemirror-model";
import { EditorState, NodeSelection, Plugin, PluginKey } from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import type { AssigneeCompletionData } from "~/actionItems/ActionItemCompletionMenu";
import ActionItemProgressPill from "~/actionItems/ActionItemProgressPill";
import { NoteBlockStatus, StatusCondition } from "~/actionItems/enum";
import { nodeAttrsToStatus, toggleNoteBlockStatus } from "~/actionItems/utils";
import type { AttendeeAttrs } from "~/common/Attendees";
import { Extension } from "~/lib/prose";
import { setBlockType } from "~/lib/prose/commands";
import { ACTION_ITEM_PROGRESS_ORDER, addAnnotation, removeAnnotation } from "~/lib/prose/plugins/blockAnnotation";
import { getBlockBoundaryState } from "~/lib/prose/plugins/blockBoundary";
import { getContext } from "~/lib/prose/plugins/context";
import { findBlockAndPos } from "~/lib/prose/utils";
import { isAcceptFlowFFOn } from "~/meetingRecorder/utils";

type ActionItemProgressState = {
	test: boolean;
};

type PillData = {
	element: Element;
	assignees: Array<AssigneeCompletionData>;
	node: Node;
};

const key = new PluginKey<ActionItemProgressState>();

export default class ActionItemProgressPlugin extends Extension {
	name = "actionItemProgress";
	pillDomNodes = new Map<string, PillData>();

	// TODO: We might want something when creating this extension, maybe the viewer id
	// Let's remove it if we end up not using this
	constructor() {
		super();
	}

	getParticipantsMap(editorView: EditorView) {
		const context = getContext(editorView.state);
		const assigneeUsersMap = new Map<string, AttendeeAttrs>();
		context?.participants?.forEach((attendee: AttendeeAttrs) => {
			const userId = attendee.id || attendee.user?.id || "";
			if (!userId) return;
			assigneeUsersMap.set(userId, attendee);
		});
		return assigneeUsersMap;
	}

	formatAssigneesData(editorView: EditorView, assignees: Array<AssigneeCompletionData>) {
		if (assignees.length === 0) return [];
		const participantsMap = this.getParticipantsMap(editorView);
		const formattedAssignees = assignees.map(assignee => {
			if (!participantsMap.has(assignee.id)) return assignee;
			return {
				...assignee,
				attendeeAttrs: participantsMap.get(assignee.id),
			};
		});
		return formattedAssignees;
	}

	isUserAnAssignee(editorView: EditorView, assignees: Array<AssigneeCompletionData>) {
		const context = getContext(editorView.state);
		const userId = context?.userId;
		return Boolean(assignees.find(assignee => assignee.id === userId));
	}

	// We will only render for action items with multiple assignees that are completed by all
	shouldRenderForNode(node: Node) {
		return (
			node.type.name === "todo" &&
			node.attrs.assignees &&
			node.attrs.assignees.length > 1 &&
			node.attrs.statusCondition &&
			node.attrs.statusCondition === StatusCondition.ALL
		);
	}

	toggleAssignee(assignee: AssigneeCompletionData, nodeKey: string) {
		if (!this.editorView || !this.pillDomNodes.get(nodeKey)) return;
		const nodeAndPos = findBlockAndPos(this.editorView.state.doc, nodeKey);
		if (!nodeAndPos) return;

		const [node, pos] = nodeAndPos;
		// Update assignees list
		if (!node.attrs.assignees) return;
		const assignees = [...node.attrs.assignees];
		let newStatus = NoteBlockStatus.NOT_DONE;
		for (let i = 0; i < assignees.length; i++) {
			if (assignees[i].id !== assignee.id) continue;
			newStatus = toggleNoteBlockStatus(assignees[i].status);
			assignees[i] = {
				...assignees[i],
				status: newStatus,
			};
			break;
		}
		// Check if we need to toggle the main status
		const nodeStatus = nodeAttrsToStatus(node.attrs);
		let attrsUpdates: { assignees: AssigneeCompletionData[]; done?: boolean; aiGenerated?: boolean } = { assignees };
		// If the new assignee status might change the note block status
		if (newStatus !== nodeStatus) {
			// If we are toggling to done we should check if everyone is done/wont_do
			if (
				newStatus === NoteBlockStatus.DONE &&
				assignees.every(
					assignee => assignee.status === NoteBlockStatus.DONE || assignee.status === NoteBlockStatus.WONT_DO,
				)
			) {
				attrsUpdates = {
					...attrsUpdates,
					done: true,
				};
			} else {
				attrsUpdates = {
					...attrsUpdates,
					done: false,
				};
			}
		}

		// If the action item is AI-generated, toggling the status should accept it
		if (isAcceptFlowFFOn() && node.attrs.aiGenerated) {
			attrsUpdates = {
				...attrsUpdates,
				aiGenerated: false,
			};
		}

		setBlockType(
			node.type,
			{ ...node.attrs, ...attrsUpdates },
			NodeSelection.create(this.editorView.state.doc, pos),
		)(this.editorView.state, this.editorView.dispatch);
	}

	assignYourself(nodeKey: string) {
		if (!this.editorView || !this.pillDomNodes.get(nodeKey)) return;
		const context = getContext(this.editorView.state);
		const userId = context?.userId;
		if (!userId) return;
		const nodeAndPos = findBlockAndPos(this.editorView.state.doc, nodeKey);
		if (!nodeAndPos) return;

		const [node, pos] = nodeAndPos;
		// Update assignees list
		const assignees = node.attrs.assignees ? [...node.attrs.assignees] : [];
		assignees.push({
			id: userId,
			status: NoteBlockStatus.NOT_DONE,
		});
		const attrs = {
			assignees,
			done: false,
			wontDo: false,
		};
		setBlockType(
			node.type,
			{ ...node.attrs, ...attrs },
			NodeSelection.create(this.editorView.state.doc, pos),
		)(this.editorView.state, this.editorView.dispatch);
	}

	createPill(editorView: EditorView, node: Node, assignees: Array<AssigneeCompletionData>) {
		const nodeKey = node.attrs.blockId;
		if (this.pillDomNodes.has(nodeKey)) {
			this.updatePill(editorView, nodeKey, assignees);
			return;
		}
		const element = document.createElement("div");
		element.contentEditable = "false";
		const formattedAssignees = this.formatAssigneesData(editorView, assignees);
		ReactDOM.render(
			<ActionItemProgressPill
				assignees={formattedAssignees}
				showAssignYourself={!this.isUserAnAssignee(editorView, assignees)}
				// eslint-disable-next-line react/jsx-no-bind
				onToggle={assignee => this.toggleAssignee(assignee, nodeKey)}
				// eslint-disable-next-line react/jsx-no-bind
				onAssignYourself={() => this.assignYourself(nodeKey)}
			/>,
			element,
		);
		addAnnotation(editorView, nodeKey, element, ACTION_ITEM_PROGRESS_ORDER);
		this.pillDomNodes.set(nodeKey, { element, assignees, node });
	}

	// TODO: Improve type for assignees
	updatePill(editorView: EditorView, node: Node, assignees: Array<AssigneeCompletionData>) {
		const nodeKey = node.attrs.blockId;
		const elementData = this.pillDomNodes.get(nodeKey);
		if (!elementData) return;
		// Check if the assignees list changes, otherwise we don't need to rerender this
		const { element, assignees: currentAssignees } = elementData;
		let assigneesChanged = false;
		if (currentAssignees.length !== assignees.length) {
			assigneesChanged = true;
		} else {
			const currentAssigneesMap = new Map<string, string>();
			currentAssignees.forEach(assignee => currentAssigneesMap.set(assignee.id, assignee.status));
			for (let i = 0; i < assignees.length; i++) {
				const existingAssigneeStatus = currentAssigneesMap.get(assignees[i].id);
				if (!existingAssigneeStatus || existingAssigneeStatus !== assignees[i].status) {
					assigneesChanged = true;
					break;
				}
			}
		}
		if (!assigneesChanged) return;
		const formattedAssignees = this.formatAssigneesData(editorView, assignees);
		ReactDOM.render(
			<ActionItemProgressPill
				assignees={formattedAssignees}
				showAssignYourself={!this.isUserAnAssignee(editorView, assignees)}
				// eslint-disable-next-line react/jsx-no-bind
				onToggle={assignee => this.toggleAssignee(assignee, nodeKey)}
				// eslint-disable-next-line react/jsx-no-bind
				onAssignYourself={() => this.assignYourself(nodeKey)}
			/>,
			element,
		);
		this.pillDomNodes.set(nodeKey, { element, assignees, node });
	}

	removePill(editorView: EditorView, nodeKey: string) {
		const elementData = this.pillDomNodes.get(nodeKey);
		if (!elementData) return;
		const { element } = elementData;
		ReactDOM.unmountComponentAtNode(element);
		removeAnnotation(editorView, nodeKey, element);
		element.remove();
		this.pillDomNodes.delete(nodeKey);
	}

	updatePills(state: EditorState) {
		const editorView = this.editorView;
		if (!editorView) return;

		// TODO: For sure there's a better way to do this (access to all action items)
		state.doc.content.forEach((node: Node) => {
			const nodeKey = node.attrs.blockId;
			if (!nodeKey) return;
			// Check if we should render
			if (!this.shouldRenderForNode(node)) {
				// If we should not render but we are remove it
				if (this.pillDomNodes.has(nodeKey)) {
					this.removePill(editorView, nodeKey);
				}
				return;
			}

			const assignees = [...node.attrs.assignees];
			// Check if we are updating or creating a new pill
			if (this.pillDomNodes.has(nodeKey)) {
				this.updatePill(editorView, node, assignees);
				return;
			}
			this.createPill(editorView, node, assignees);
		});

		// Remove pills that belongs to blocks that doesn't exist anymore
		const blockBoundaryState = getBlockBoundaryState(state);
		if (!blockBoundaryState) return;
		const { blockBoundaries } = blockBoundaryState;

		[...this.pillDomNodes.keys()].forEach(blockKey => {
			if (blockKey in blockBoundaries) return;
			this.removePill(editorView, blockKey);
		});
	}

	get plugins() {
		return [
			new Plugin<ActionItemProgressState>({
				key,
				state: {
					init: (_config, state: EditorState) => {
						this.updatePills(state);
						return { test: true };
					},
					apply: (tr, value, _oldState, newState) => {
						this.updatePills(newState);
						return { test: true };
					},
				},
			}),
		];
	}

	destroy = () => {
		this.pillDomNodes.forEach(({ element }) => {
			ReactDOM.unmountComponentAtNode(element);
			element.remove();
		});
	};
}
